/**
 * Cookie consent for optional analytics.
 *
 * The choice lives in a `noctua_consent` cookie scoped to `.noctua.uno`, so a
 * choice made on the landing page (noctua.uno) carries over to the web app
 * (cast.noctua.uno) and back. The landing page mirrors this logic in
 * landingpage/static/js/consent.js — keep the two in step.
 */

export type ConsentState = 'granted' | 'denied';

export const CONSENT_COOKIE = 'noctua_consent';
export const CONSENT_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

/** Fired on window after the stored choice changes. detail: ConsentState */
export const CONSENT_CHANGE_EVENT = 'noctua:consent-change';
/** Fired on window to reopen the consent banner ("cookie settings"). */
export const CONSENT_OPEN_EVENT = 'noctua:consent-open';

const SHARED_DOMAIN = 'noctua.uno';

/** `.noctua.uno` on noctua hosts; null (host-only cookie) everywhere else. */
export function consentCookieDomain(hostname: string): string | null {
  return hostname === SHARED_DOMAIN || hostname.endsWith(`.${SHARED_DOMAIN}`)
    ? `.${SHARED_DOMAIN}`
    : null;
}

/** The landing page that pairs with this app: cast.noctua.uno → noctua.uno,
    test.cast.noctua.uno → test.noctua.uno. */
export function privacyPolicyUrl(hostname: string): string {
  const landingHost = consentCookieDomain(hostname) ? hostname.replace('cast.', '') : SHARED_DOMAIN;
  return `https://${landingHost}/privacy`;
}

export function readConsent(): ConsentState | null {
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CONSENT_COOKIE}=`));
  const value = match?.slice(CONSENT_COOKIE.length + 1);
  return value === 'granted' || value === 'denied' ? value : null;
}

export function writeConsent(state: ConsentState): void {
  const domain = consentCookieDomain(window.location.hostname);
  const parts = [
    `${CONSENT_COOKIE}=${state}`,
    'Path=/',
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
  window.dispatchEvent(new CustomEvent<ConsentState>(CONSENT_CHANGE_EVENT, { detail: state }));
}

/** Reopen the consent banner so the user can change their choice. */
export function openConsentSettings(): void {
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

/** Remove the Google Analytics cookies (`_ga`, `_ga_<id>`, `_gid`) after a reject. */
export function clearAnalyticsCookies(): void {
  const names = document.cookie
    .split(';')
    .map((part) => part.trim().split('=')[0])
    .filter((name) => name === '_ga' || name === '_gid' || name.startsWith('_ga_'));
  const domain = consentCookieDomain(window.location.hostname);
  const expire = 'Path=/; Max-Age=0';
  for (const name of names) {
    document.cookie = `${name}=; ${expire}`;
    if (domain) document.cookie = `${name}=; ${expire}; Domain=${domain}`;
  }
}
